import React from 'react';

import { observer } from 'mobx-react';

import DataError from 'containers/Error/DataError';

import { timezoneStore } from 'stores/Timezone';

import { Store } from 'ui/stores/DetailsActivities/types';
import { ActivityResponse, SeparatorResponse } from 'ui/types';
import { ActivityType } from 'utils/stores/Activities/constants';
import { readerDayKey } from 'utils/formatters/timezone';

import { FeedContainer, LoadMore, SpinnerContainer } from './styles';
import Comment from './Comment';
import File from './File';
import FileUploadProgress from './FileUploadProgress';
import { oldestFirst } from './groupByDay';
import { FeedProps, Mapping } from './types';
import Separator from './Separator';

export const DefaultFeedMapping = {
  message: Comment,
  file: File,
  file_upload_progress: FileUploadProgress,
  separator: Separator,
};

// With the flag off the feed keeps the UTC day it has always grouped by, not the browser's.
const dayOf = (activity: ActivityResponse): string =>
  timezoneStore.enabled ? readerDayKey(activity.created) : activity.created.split('T')[0];

export function groupAndSortActivities(
  activities: ActivityResponse[] | undefined,
  mapping: Mapping
): ActivityResponse[] {
  if (!activities?.length) return [];

  const keyed = oldestFirst(activities, (activity) => activity.created).map((activity) => ({
    activity,
    day: dayOf(activity),
  }));
  const unmappedDays = new Set(keyed.filter(({ activity }) => !(activity.type in mapping)).map(({ day }) => day));
  const headedDays = new Set<string>();

  return keyed.flatMap(({ activity, day }) => {
    if (headedDays.has(day) || unmappedDays.has(day)) return [activity];
    headedDays.add(day);
    const separator: SeparatorResponse = { uuid: `${day}_generated_uuid`, created: day, type: ActivityType.Separator };
    return [separator, activity];
  });
}

function checkAdditionalElements<T>(array1: T[], array2: T[]): 'beginning' | 'end' | 'none' {
  const minLength = Math.min(array1.length, array2.length);

  const isPrefix = array1.slice(0, minLength).every((element, index) => element === array2[index]);
  const isSuffix = array1.slice(-minLength).every((element, index) => element === array2[index]);

  if (isPrefix && array1.length > array2.length) {
    return 'end';
  }
  if (isSuffix && array1.length > array2.length) {
    return 'beginning';
  }
  if (array1.length === array2.length && isPrefix) {
    return 'none';
  }
  return 'none';
}

function Feed<T extends Store, S>({
  originalActivities,
  className,
  onSizeChanged,
  // @ts-ignore
  mapping = DefaultFeedMapping,
  // nonEditable,
  disableAutoScroll,
  ...props
}: FeedProps<T, S>): React.ReactElement {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [prevActivities, setPrevActivities] = React.useState<ActivityResponse[]>([]);
  const [disableAutoScrollInternal, setDisableAutoScrollInternal] = React.useState(disableAutoScroll);
  const activitiesToRender = originalActivities.activities || prevActivities;
  const checkResult = checkAdditionalElements(
    activitiesToRender.map((act) => act.uuid),
    prevActivities.map((act) => act.uuid)
  );

  React.useEffect(() => {
    if (
      containerRef.current &&
      containerRef.current.lastElementChild &&
      containerRef.current.childNodes.length > 1 &&
      !originalActivities.isLoading &&
      !disableAutoScrollInternal
    ) {
      setPrevActivities(activitiesToRender);
      if (checkResult !== 'end') {
        return;
      }
      setTimeout(() => {
        containerRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 200);
    }
  }, [checkResult, originalActivities.isLoading, activitiesToRender, disableAutoScrollInternal]);

  React.useEffect(() => {
    if (originalActivities.activities === undefined) {
      originalActivities.loadActivities();
    }
  }, [originalActivities]);

  React.useLayoutEffect(() => {
    if (containerRef.current === null) {
      return () => undefined;
    }

    const heightObserver = new ResizeObserver(() => {
      if (containerRef.current && onSizeChanged) {
        onSizeChanged(containerRef.current.scrollHeight);
      }
    });
    heightObserver.observe(containerRef.current);

    return () => {
      heightObserver.disconnect();
    };
  }, [containerRef, onSizeChanged]);

  if (originalActivities.error) {
    return (
      <FeedContainer className={className}>
        <DataError onReload={originalActivities.loadActivities} message="We are unable to load activities" />
      </FeedContainer>
    );
  }

  return (
    <FeedContainer className={className} ref={containerRef}>
      {!originalActivities.isLoading && originalActivities.hasMore && (
        <LoadMore
          onClick={() => {
            setDisableAutoScrollInternal(true);
            originalActivities.loadActivities();
          }}
        >
          Show previous updates
        </LoadMore>
      )}
      {originalActivities.isLoading && (
        <SpinnerContainer>
          <img src="/spinner.gif" alt="progress spinner" width={25} height={25} />
        </SpinnerContainer>
      )}
      {groupAndSortActivities(activitiesToRender, mapping).map((activity): React.ReactElement | null => {
        if (activity.type in mapping) {
          const Component = mapping[activity.type];
          return <Component activity={activity} key={activity.uuid} {...props} />;
        }
        return null;
      })}
    </FeedContainer>
  );
}

export default observer(Feed);
